/**
 * Stripe Service
 *
 * Handles Stripe integration for subscription management, checkout sessions,
 * and customer portal access.
 */

import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { supabase, isDemoMode } from '@/lib/supabase';
import type {
  SubscriptionTier,
  BillingInterval,
  StripePrices,
  CheckoutSessionOptions,
  CheckoutSessionResponse,
  CustomerPortalOptions,
  CustomerPortalResponse,
  SubscriptionDetails,
  StripeInvoice,
  PricingTierInfo,
  TrialInfo,
  EarlyAdopterInfo,
} from '@/types/stripe';

// ============================================================================
// Configuration
// ============================================================================

// Stripe publishable key from environment (support both Vite and Next.js)
const STRIPE_PUBLISHABLE_KEY =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY) ||
  '';

// Stripe promise for React components
let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise || Promise.resolve(null);
}

// Trial configuration
export const TRIAL_DAYS = 7;
export const EARLY_ADOPTER_DISCOUNT = 0.5; // 50% off

/**
 * Stripe Price IDs
 * These are the live price IDs from Stripe Dashboard
 */
export const STRIPE_PRICES: StripePrices = {
  starter: {
    monthly: 'price_1SotoIHPqxuUD1TPXhfQX62J',  // $29/month
    annual: 'price_1SotpwHPqxuUD1TPNeD0njrR',   // $300/year
    earlyBird: 'price_1SotoIHPqxuUD1TPXhfQX62J', // Uses monthly with coupon
  },
  professional: {
    monthly: 'price_1SotpFHPqxuUD1TPFhridrLI',  // $79/month
    annual: 'price_1SotqWHPqxuUD1TP2IVnhzeH',   // $800/year
    earlyBird: 'price_1SotpFHPqxuUD1TPFhridrLI', // Uses monthly with coupon
  },
  enterprise: {
    monthly: 'price_1Sotr3HPqxuUD1TPHD32xApT',  // $200/month
    annual: 'price_1SotrQHPqxuUD1TPqNwT1TCM',   // $2000/year
    earlyBird: 'price_1Sotr3HPqxuUD1TPHD32xApT', // Uses monthly with coupon
  },
};

// ============================================================================
// Pricing Information
// ============================================================================

export const PRICING_TIERS: PricingTierInfo[] = [
  {
    name: 'Free',
    tier: 'free',
    description: 'For small properties getting started',
    monthlyPrice: 0,
    annualPrice: 0,
    features: [
      'Up to 5 units',
      'Basic reporting',
      'Work order management',
      'Email support',
    ],
    limits: {
      units: 5,
      users: 2,
      properties: 1,
      storage: '1GB',
    },
  },
  {
    name: 'Starter',
    tier: 'starter',
    description: 'For small properties and teams',
    monthlyPrice: 29,
    annualPrice: 300,
    earlyBirdMonthlyPrice: 14.50,
    earlyBirdAnnualPrice: 150,
    features: [
      'Up to 25 units',
      'QR code workflows',
      'Asset tracking',
      'Vendor management',
      'IoT device basics',
      'SMS notifications',
      'Priority email support',
    ],
    limits: {
      units: 25,
      users: 5,
      properties: 3,
      storage: '10GB',
    },
  },
  {
    name: 'Professional',
    tier: 'professional',
    description: 'For growing portfolios',
    monthlyPrice: 79,
    annualPrice: 800,
    earlyBirdMonthlyPrice: 39.50,
    earlyBirdAnnualPrice: 400,
    features: [
      'Up to 100 units',
      'Magic link vendor portal',
      'Floor plan mapping',
      'Advanced analytics',
      'All IoT sensors',
      'API access',
      'Webhook integrations',
      'Phone support',
    ],
    limits: {
      units: 100,
      users: 15,
      properties: 10,
      storage: '50GB',
    },
    highlighted: true,
    badge: 'Most Popular',
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    description: 'For large-scale operations',
    monthlyPrice: 200,
    annualPrice: 2000,
    earlyBirdMonthlyPrice: 100,
    earlyBirdAnnualPrice: 1000,
    features: [
      'Unlimited units',
      'SSO authentication',
      'Audit logging',
      'White-label branding',
      'Custom integrations',
      'Dedicated support',
      'SLA guarantees',
      'Training sessions',
    ],
    limits: {
      units: 'unlimited',
      users: 'unlimited',
      properties: 'unlimited',
      storage: 'Unlimited',
    },
  },
];

// ============================================================================
// Checkout & Portal
// ============================================================================

/**
 * Create a Stripe Checkout Session
 * Redirects user to Stripe-hosted checkout page
 */
export async function createCheckoutSession(
  options: CheckoutSessionOptions
): Promise<CheckoutSessionResponse> {
  if (isDemoMode()) {
    throw new Error('Checkout is not available in demo mode');
  }

  const { data, error } = await supabase.functions.invoke('stripe-checkout', {
    body: {
      priceId: options.priceId,
      organizationId: options.organizationId,
      userId: options.userId,
      successUrl: options.successUrl || `${window.location.origin}/billing?success=true`,
      cancelUrl: options.cancelUrl || `${window.location.origin}/billing?canceled=true`,
      trialDays: options.trialDays ?? TRIAL_DAYS,
      couponCode: options.couponCode,
      isEarlyAdopter: options.isEarlyAdopter,
    },
  });

  if (error) {
    console.error('Checkout session error:', error);
    throw new Error('Failed to create checkout session');
  }

  return data as CheckoutSessionResponse;
}

/**
 * Create a Stripe Customer Portal session
 * Allows customers to manage their subscription
 */
export async function createCustomerPortalSession(
  options: CustomerPortalOptions
): Promise<CustomerPortalResponse> {
  if (isDemoMode()) {
    throw new Error('Customer portal is not available in demo mode');
  }

  const { data, error } = await supabase.functions.invoke('stripe-portal', {
    body: {
      customerId: options.customerId,
      returnUrl: options.returnUrl || `${window.location.origin}/billing`,
    },
  });

  if (error) {
    console.error('Customer portal error:', error);
    throw new Error('Failed to create portal session');
  }

  return data as CustomerPortalResponse;
}

/**
 * Redirect to Stripe Checkout
 * In Stripe.js v8+, we use URL-based redirect instead of deprecated redirectToCheckout
 */
export function redirectToCheckout(checkoutUrl: string): void {
  if (!checkoutUrl) {
    throw new Error('No checkout URL provided');
  }
  window.location.href = checkoutUrl;
}

// ============================================================================
// Subscription Management
// ============================================================================

/**
 * Get subscription details for an organization
 */
export async function getSubscriptionDetails(
  organizationId: string
): Promise<SubscriptionDetails | null> {
  if (isDemoMode()) {
    return {
      stripe_customer_id: null,
      stripe_subscription_id: null,
      stripe_price_id: null,
      subscription_tier: 'professional', // Demo mode gets professional features
      subscription_status: 'active',
      trial_ends_at: null,
      current_period_end: null,
      is_early_adopter: false,
      cancel_at_period_end: false,
    };
  }

  const { data, error } = await supabase
    .from('organizations')
    .select(`
      stripe_customer_id,
      stripe_subscription_id,
      stripe_price_id,
      subscription_tier,
      subscription_status,
      trial_ends_at,
      current_period_end,
      is_early_adopter
    `)
    .eq('id', organizationId)
    .single();

  if (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }

  return {
    ...data,
    cancel_at_period_end: false, // Would come from Stripe API
  } as SubscriptionDetails;
}

/**
 * Get the price ID for a tier and interval
 */
export function getPriceId(
  tier: Exclude<SubscriptionTier, 'free'>,
  interval: BillingInterval,
  isEarlyAdopter = false
): string {
  const tierPrices = STRIPE_PRICES[tier];

  if (isEarlyAdopter && tierPrices.earlyBird) {
    return tierPrices.earlyBird;
  }

  return interval === 'monthly' ? tierPrices.monthly : tierPrices.annual;
}

/**
 * Map Stripe price ID to subscription tier
 */
export function getTierFromPriceId(priceId: string): SubscriptionTier {
  for (const [tier, prices] of Object.entries(STRIPE_PRICES)) {
    if (
      prices.monthly === priceId ||
      prices.annual === priceId ||
      prices.earlyBird === priceId
    ) {
      return tier as SubscriptionTier;
    }
  }
  return 'free';
}

// ============================================================================
// Trial & Early Adopter
// ============================================================================

/**
 * Check if organization is in trial period
 */
export function getTrialInfo(trialEndsAt: string | null): TrialInfo {
  if (!trialEndsAt) {
    return { isInTrial: false, trialEndsAt: null, daysRemaining: 0 };
  }

  const now = new Date();
  const trialEnd = new Date(trialEndsAt);
  const isInTrial = trialEnd > now;
  const daysRemaining = isInTrial
    ? Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return { isInTrial, trialEndsAt, daysRemaining };
}

/**
 * Get early adopter discount info
 */
export function getEarlyAdopterInfo(
  isEarlyAdopter: boolean,
  expiresAt?: string | null
): EarlyAdopterInfo {
  return {
    isEarlyAdopter,
    discountPercent: isEarlyAdopter ? EARLY_ADOPTER_DISCOUNT * 100 : 0,
    expiresAt: expiresAt || null,
  };
}

// ============================================================================
// Invoices
// ============================================================================

/**
 * Get invoices for an organization
 */
export async function getInvoices(organizationId: string): Promise<StripeInvoice[]> {
  if (isDemoMode()) {
    return [];
  }

  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching invoices:', error);
    return [];
  }

  return data as StripeInvoice[];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format price for display
 */
export function formatPrice(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Calculate savings for annual billing
 */
export function calculateAnnualSavings(monthlyPrice: number): {
  annualTotal: number;
  savings: number;
  savingsPercent: number;
} {
  const monthlyTotal = monthlyPrice * 12;
  const annualTotal = monthlyPrice * 12 * 0.8; // 20% off
  const savings = monthlyTotal - annualTotal;
  const savingsPercent = 20;

  return { annualTotal, savings, savingsPercent };
}

/**
 * Check if Stripe is configured
 */
export function isStripeConfigured(): boolean {
  return !!STRIPE_PUBLISHABLE_KEY && STRIPE_PUBLISHABLE_KEY !== '';
}
